import React, { useMemo, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Modal from '../modal/Modal'
import classNames from 'classnames'
import Spinner from 'react-spinkit'
import styles from './index.module.scss'
import InvoiceTransactionsTable from '../invoice/invoice-transactions-table'
import { captureError, captureErrorAndNotify } from '../../utilities/errorHandlers'
import QUERY_KEYS from '../../graphql/queryKeys'
import { useSelector } from 'react-redux'
import { generatePath } from 'react-router-dom'
import { ROUTES } from '../../routes'
import wastepayTransactionsQuery from '../../graphql/queries/wastepayTransactions'
import dispatcherTransactionsQuery from '../../graphql/queries/dispatcherTransactions'
import invoiceJobEmailsQuery from '../../graphql/queries/invoiceJobEmails'
import { exportTransactionToQBO } from '../../pages/invoice-page/utils'
import exportRefundToQuickbooksMutation from '../../graphql/mutations/exportRefundToQuickbooks'
import exportPaymentToQuickbooksMutation from '../../graphql/mutations/exportPaymentToQuickbooks'
import useMutation from '../../hooks/useMutation'
import useQuery, { generateQueryKey } from '../../hooks/useQuery'

import notify from '../../utilities/notify'
import { useQueryClient } from 'react-query'

export default function SendReceiptModal ({
  isOpen,
  onClose,
  onSubmit,
  emails,
  clientEmail,
  emailInput,
  setEmailInput,
  emailError,
  actionLoading,
  invoice,
  fromInvoiceList = false,
  invoicer,
  selectedTransaction,
  setSelectedTransaction
}) {
  const { data: invoiceJobEmails, isFetching: isInvoiceEmailsFetching } = useQuery(
    [QUERY_KEYS.invoiceJobEmails, invoice?.id],
    invoiceJobEmailsQuery,
    {
      enabled: Boolean(invoice?.id) && !emails?.length,
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve invoice job emails.')
      }
    }
  )

  const hasInitializedRef = useRef(false)

  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false
      return
    }
    if (isOpen && !hasInitializedRef.current && !isInvoiceEmailsFetching) {
      const invoiceEmailsArray = invoiceJobEmails?.invoiceJobEmails?.jobEmails ?? []
      const uniqueEmails = [
        ...new Set([...invoiceEmailsArray, clientEmail].filter(Boolean))
      ]
      setEmailInput(emails.length ? emails.join(', ') : uniqueEmails.join(', '))
      hasInitializedRef.current = true
    }
  }, [isOpen, emails, setEmailInput, invoiceJobEmails, isInvoiceEmailsFetching, clientEmail])

  const { user, hauler } = useSelector(({ user }) => ({ user: user.user, hauler: user.hauler }))
  const queryClient = useQueryClient()

  const hasInvoiceTickets = useMemo(() => {
    return invoice?.tickets?.length > 0
  }, [invoice])

  const { data: transactions, isError: wpTranscationError, isFetching: wpTransactionsFetching } = useQuery(
    [QUERY_KEYS.wastepayTransactions, { invoiceId: invoice?.id }],
    wastepayTransactionsQuery,
    {
      enabled: hauler.isWastepayConnected && Boolean(invoice?.invoiceNumber) && fromInvoiceList,
      onError: (error) => {
        captureError(error)
      }
    }
  )

  const { data: dispatcherTransactions, isError: dispatcherTranscationError, isFetching: dispTransactionsFetching } = useQuery(
    [QUERY_KEYS.dispatcherTransactions, { invoiceId: invoice?.uuid }],
    dispatcherTransactionsQuery,
    {
      enabled: Boolean(invoice?.uuid) && fromInvoiceList,
      onError: (error) => {
        captureError(error)
      }
    }
  )

  const { mutateAsync: exportRefundToQuickbooks } = useMutation(exportRefundToQuickbooksMutation, {
    onSuccess (data, variables) {
      updateTransactionCache(variables.invoiceId, data.refund)
      notify('success', `Transaction ${variables.transactionId} was successfully exported to QuickBooks`)
    },
    onError (error, variables) {
      captureErrorAndNotify(error, `Failed to export transaction ${variables.transactionId} to QuickBooks`)
    }
  })
  const { mutateAsync: exportPaymentToQuickbooks } = useMutation(exportPaymentToQuickbooksMutation, {
    onSuccess (data, variables) {
      updateTransactionCache(variables.invoiceId, data.payment)
    },
    onError (error, variables) {
      captureErrorAndNotify(error, `Failed to export transaction ${variables.transactionId} to QuickBooks`)
    }
  })

  function updateTransactionCache (invoiceId, transaction) {
    queryClient.setQueryData(generateQueryKey([QUERY_KEYS.wastepayTransactions, { invoiceId }], user.id), (oldData) => ({
      ...(oldData ?? {}),
      wastepayTransactions: {
        ...(oldData?.wastepayTransactions ?? {}),
        ...transaction
      }
    }))
  }

  async function handleTransactionQboExport (invoice, transaction) {
    return exportTransactionToQBO(invoice, transaction, exportPaymentToQuickbooks, exportRefundToQuickbooks)
  }

  const paymentPath = useMemo(() => {
    if (!invoice) return ''
    return generatePath(ROUTES.invoicePayment, { id: invoice?.id })
  }, [invoice])

  const getDisabledState = () => {
    if (fromInvoiceList) {
      return !emailInput?.length || !selectedTransaction
    }
    return !emailInput?.length
  }

  return (
    <Modal isOpen={isOpen} closeModal={onClose}>
      <form
        name='send-receipt-capture-email'
        className='dis-modal-inner'
        onSubmit={onSubmit}
        onReset={onClose}>
        <h3>Send Receipt</h3>
        {fromInvoiceList &&
          <>
            <div>Enter recipient email address and choose receipt</div>
            <hr></hr>
            <span className='dis-list-item-header'>Recipient</span>
          </>
        }
        <label
          className={classNames(
            'dis-form-label-input-wrapper',
            styles['modal-input'],
            { 'has-error': emailError },
            { [styles.marginTop]: fromInvoiceList }
          )}>
          Email Address (comma separated)
          <textarea
            name='emails'
            autoComplete='off'
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            rows={3}
            className={styles.emailInput}
          />
          {!emailError || <h6>1 or more emails are invalid</h6>}
        </label>
        {(fromInvoiceList)
          ? (wpTransactionsFetching || dispTransactionsFetching)
              ? <Spinner name='circle' />
              : <div>
                <span className='dis-list-item-header'>Invoice Transactions</span>
                <div className={classNames('dis-panel-body', styles.transactionsTable)}>
                  <InvoiceTransactionsTable
                    hasInvoiceTickets={hasInvoiceTickets}
                    invoice={invoice}
                    paymentPath={paymentPath}
                    transactions={transactions?.wastepayTransactions ?? []}
                    dispatcherTransactions={dispatcherTransactions?.dispatcherTransactions ?? []}
                    onQboExport={handleTransactionQboExport}
                    isWPError={wpTranscationError}
                    isDispError={dispatcherTranscationError}
                    isQBOSynced={hauler.quickbooks.isConnected}
                    isWastepayConnected={hauler.isWastepayConnected}
                    invoicer={invoicer}
                    fromInvoiceList={fromInvoiceList}
                    selectedTransaction={selectedTransaction}
                    setSelectedTransaction={setSelectedTransaction}
                  />
                </div>
              </div>
          : <></>
        }

        <div className={styles['modal-button-wrapper']}>
          <button type='reset' className='dis-btn dis-btn-sm dis-btn-no-shadow'>
            Close
          </button>

          <button
            type='submit'
            className='dis-btn dis-btn-sm dis-btn-primary'
            disabled={getDisabledState()}>
            Send
            {actionLoading
              ? <Spinner name='circle' className={styles['spinner-left']} fadeIn='none' />
              : <span className='material-icons'>send</span>
            }
          </button>
        </div>
      </form>
    </Modal>
  )
}

SendReceiptModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  emails: PropTypes.arrayOf(PropTypes.string),
  clientEmail: PropTypes.string,
  emailInput: PropTypes.string,
  setEmailInput: PropTypes.func.isRequired,
  emailError: PropTypes.bool,
  actionLoading: PropTypes.bool,
  invoice: PropTypes.object,
  fromInvoiceList: PropTypes.bool,
  invoicer: PropTypes.string,
  selectedTransaction: PropTypes.object,
  setSelectedTransaction: PropTypes.func
}
